import { Component, Input, OnInit } from '@angular/core';
import { round, sortBy, toNumber } from 'lodash';
import { CurrentSchoolYear } from 'Src/ng2/shared/constants/current-school-year.constant';
import { IAssessmentHistoryItem, IStudent } from '../../../../shared/typings/interfaces/student.interface';
import { IReadyPanelDataObj, IStudentBreakoutScore } from '../student-academic.interface';

@Component({
  selector: 'academic-panel-ready',
  template: require('./academic-panel-ready.html'),
  styles: [require('./academic-panel-ready.component.scss')],
})
export class AcademicPanelReady implements OnInit {
  @Input() student: IStudent;
  @Input() examName: string;

  // overall scores - the ones that are always visible
  elaOverallData: IReadyPanelDataObj[];
  mathOverallData: IReadyPanelDataObj[];

  // expanded scores. Shown when overall score row is expanded
  elaBreakoutData: IStudentBreakoutScore[];
  mathBreakoutData: IStudentBreakoutScore[];

  // toggles to control visibility of expanded score rows
  showElaSub: boolean = false;
  showMathSub: boolean = false;

  currentSchoolYearWithSyPrefix: string;

  private colLimit: number = 4; // no. of columns to show on the UI

  ngOnInit(): void {
    this.currentSchoolYearWithSyPrefix = CurrentSchoolYear.WITH_SY_PREFIX;
    this.setUI();
  }

  setUI(): void {
    const elaHistory = this.getSubjectHistoryForYear(
      this.student.assmnts.history,
      this.examName,
      'ELA',
      this.currentSchoolYearWithSyPrefix,
    );
    this.elaOverallData = this.getOverallData(elaHistory, 'Overall');
    this.elaBreakoutData = this.getBreakoutData(elaHistory);

    const mathHistory = this.getSubjectHistoryForYear(
      this.student.assmnts.history,
      this.examName,
      'Math',
      this.currentSchoolYearWithSyPrefix,
    );
    this.mathOverallData = this.getOverallData(mathHistory, 'Overall');
    this.mathBreakoutData = this.getBreakoutData(mathHistory);
  }

  getOverallData(historyCollection: IAssessmentHistoryItem[], skillName: string): IReadyPanelDataObj[] {
    const formattedResult = Array(this.colLimit).fill(null);
    let skillHistory = this.getSkillHistory(historyCollection, skillName, true);

    skillHistory = sortBy(skillHistory, (history: any) => {
      return history.admin;
    });

    skillHistory.forEach(history => {
      const adminLevel = history.admin;
      const displayData = this.examName === 'Ready' ? history.score : history.absLvl;
      let tooltipData = '';

      if (this.examName === 'iReady') {
        tooltipData = 'Score: ' + history.score + '\nRelative: ' + history.relLvl;
      }

      if (adminLevel <= this.colLimit) {
        formattedResult[adminLevel - 1] = { data: displayData, tooltip: tooltipData };
      }
    });

    return formattedResult.map(score => this.getFormattedData(score));
  }

  getBreakoutData(history: IAssessmentHistoryItem[]): IStudentBreakoutScore[] {
    const breakoutSkills = this.getSkillHistory(history, 'Overall', false);

    const uniqueSkills = breakoutSkills.reduce((acc, skillObj) => {
      if (acc.indexOf(skillObj.skill) === -1) {
        acc.push(skillObj.skill);
      }
      return acc;
    }, []);

    return uniqueSkills.reduce((acc, skill) => {
      const formattedSkill = {
        name: skill,
        values: Array(this.colLimit).fill(null),
      };

      const skillHistory = this.getSkillHistory(history, skill, true);
      formattedSkill.values = this.getOverallData(skillHistory, skill);

      acc.push(formattedSkill);
      return acc;
    }, []);
  }

  getSkillHistory(history, skill: string, includeSkill: boolean): IAssessmentHistoryItem[] {
    if (includeSkill) {
      return history.filter(val => val.skill === skill);
    } else {
      return history.filter(val => val.skill !== skill);
    }
  }

  toggleSub(section: string): void {
    if (section === 'ela') {
      this.showElaSub = !this.showElaSub;
    } else if (section === 'math') {
      this.showMathSub = !this.showMathSub;
    }
  }

  getSubjectHistoryForYear(
    studentHistory: any,
    historyName: string,
    subject: string,
    year: string,
  ): IAssessmentHistoryItem[] {
    return studentHistory.filter(val => val.name === historyName && val.subj === subject && val.year === year);
  }

  getFormattedData(dataObj: IReadyPanelDataObj): IReadyPanelDataObj {
    if (dataObj === null || dataObj.data === 0 || dataObj.data === null) {
      return { data: '<span class="disabled">—</span>', tooltip: '' };
    }
    if (this.examName === 'Ready') {
      dataObj.data = round(toNumber(dataObj.data) * 100, 0) + '%';
      return dataObj;
    } else if (this.examName === 'iReady') {
      return dataObj;
    }
  }
}
